<template v-if="memberships.length">
  <form class="form" novalidate @submit.prevent="handleSubmit">
    <FormErrorsList v-if="errorsMessages.length" :title="t('global.formErrorNotification')"
      :errorsList="errorsMessages" />

    <Fieldset :title="t(`memberships.form.unitPriceCorpo`)">
      <MembershipForm
        v-for="membership in corporatePlans" :key="membership.id"
        :value="membership"
        @plan-updated="updateMembership"
      />
    </Fieldset>
    <Fieldset :title="t(`memberships.form.unitPriceRealEstateBroker`)">
      <MembershipForm
        v-for="membership in realEstateBrokerPlans" :key="membership.id"
        :value="membership"
        @plan-updated="updateMembership"
      />
    </Fieldset>
    <Fieldset :title="t(`memberships.form.unitPriceCoOwner`)">
      <MembershipForm
        v-for="membership in coOwnerPlans" :key="membership.id"
        :value="membership"
        @plan-updated="updateMembership"
      />
    </Fieldset>
    <Legend :title="t(`memberships.form.unitPriceRegular`)" ></Legend>
    <Fieldset v-for="(plans,chapter) in groupedByChapterSyndicatePlans" :key="chapter" :title="chapter">
        <MembershipForm
          v-for="(plan, index) in plans" :key="`${chapter}-${index}`"
          :value="plan"
          @plan-updated="updateMembership"/>
    </Fieldset>

    <Fieldset :title="t(`memberships.tax`)">
      <FormRow with-three-columns>
        <FormInput
                :ref="`taxNameEl`"
                :name="`taxName`"
                type="text"
                :label="t(`memberships.form.taxName`)"
                v-model="taxName" />
      </FormRow>
    </Fieldset>

    <FormTooltip>
      <p><strong>{{ t('memberships.form.invalidTaxTypeMessage') }}</strong></p>
    </FormTooltip>
    <button class="form__submit btn btn--fullscreen">{{ t('global.save') }}</button>
  </form>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref, watch } from "vue";
import FormTooltip from "@/components/forms/FormTooltip.vue";
import FormInput from "@/components/forms/FormInput.vue";
import FormRow from "@/components/forms/FormRow.vue";
import FormErrorsList from "@/components/forms/FormErrorsList.vue";
import Fieldset from "@/components/forms/Fieldset.vue";
import MembershipForm from "@/components/memberships/MembershipForm.vue";
import Legend from "@/components/layout/Legend.vue";
import { useMembershipService } from "@/inversify.config";

import { useI18n } from "vue3-i18n";
import { notifyError, notifySuccess } from "@/notify";
import { SucceededOrNotResponse } from "@/types/responses/succeededOrNotResponse";
import {Plan, PlanType} from "@/types/entities/plan";

const { t } = useI18n()
const membershipService = useMembershipService()

const memberships = ref<Plan[]>([])
const taxName = ref()
const coOwnerPlans = computed(() => memberships.value.filter(x => x.type == PlanType.CoOwner))
const corporatePlans = computed(() => memberships.value.filter(x => x.type == PlanType.Corporate))
const realEstateBrokerPlans = computed(() => memberships.value.filter(x => x.type == PlanType.RealEstateBroker))
const syndicatePlans = computed(() => memberships.value
                                                 .filter(x => x.type == PlanType.Syndicate)
                                                 .sort((a, b) => (a.unitsMin ?? 0) - (b.unitsMin ?? 0)))
const groupedByChapterSyndicatePlans = computed(() => {
  return syndicatePlans.value.reduce((result: any, obj: Plan) => {
    const chapter = obj.chapter ?? "";
    if (!result[chapter]) {
      result[chapter] = [];
    }
    result[chapter].push(obj);
    return result;
  }, {});
})

onBeforeMount(async () => {
  memberships.value = await getMemberships();
  taxName.value = memberships.value.length ? memberships.value[0].product?.accountingSoftwareProduct?.taxName : ""
})

async function getMemberships() {
  return await membershipService.getMemberships() as Plan[]
}

async function updateMemberships(value: Plan[]) {
  return await membershipService.updateMemberships(value)
}

watch(taxName, (newVal) => {
  memberships.value.forEach(x=>{
    if(x.product?.accountingSoftwareProduct != null)
      x.product.accountingSoftwareProduct.taxName = newVal
  })
})

function updateMembership(value: Plan) {
  var membership = memberships.value.find(x => x.id == value.id)
  if (membership != null) membership = value

  let accountcode = membership?.product?.accountingSoftwareProduct?.accountCode
  updateAccountCodes(accountcode?.value ?? "", accountcode?.id ?? "")

  let itemCode = membership?.product?.accountingSoftwareProduct?.itemCode
  updateItemCodes(itemCode?.value ?? "", itemCode?.id ?? "")
}

async function updateAccountCodes(value: string, id:string) {
  memberships.value =  memberships.value.map(x=> {
    let accountCode = x.product?.accountingSoftwareProduct?.accountCode;
    if(accountCode?.id == id) {
      accountCode.value = value;
    }
    return x;
  })
}

async function updateItemCodes(value: string, id:string) {
  memberships.value =  memberships.value.map(x=> {
    let itemCode = x.product?.accountingSoftwareProduct?.itemCode;
    if(itemCode?.id == id) itemCode.value = value;
    return x;
  })
}

const errorsMessages = ref<string[]>([])
function displayErrors(response: SucceededOrNotResponse) {
  response = new SucceededOrNotResponse(response.succeeded, response.errors)
  if (response?.succeeded != undefined && !response?.succeeded) {
    errorsMessages.value = response.getErrorMessages('validation.membership')
  }
  else errorsMessages.value = []
}

async function handleSubmit() {
  const valid = handleValidation()
  if (!valid) {
    notifyError(t('global.formErrorNotification'))
    return
  }

  errorsMessages.value = []
  let response = await updateMemberships(memberships.value)

  if ((response as SucceededOrNotResponse)?.succeeded != undefined && !(response as SucceededOrNotResponse)?.succeeded) {
    notifyError(t('global.formErrorNotification'))
    displayErrors(response as SucceededOrNotResponse)
  }
  else {
    notifySuccess(t('memberships.form.successMessage'))
    memberships.value = response as Plan[]
  }

}

function handleValidation() {
  let valid = memberships.value.length > 0 && memberships.value.every((x: Plan) => {
    return (x.product?.accountingSoftwareProduct?.accountCode?.value?.length ?? 0 > 0) &&
      (x.product?.accountingSoftwareProduct?.itemCode?.value?.length ?? 0 > 0) &&
      ((x.product?.price ?? 0) >= 0) &&
      (x.product?.accountingSoftwareProduct?.taxName?.length ?? 0 > 0)
  })
  return valid
}
</script>
